import { parseGameBlueprint, type GameBlueprint } from "../../blueprint";
import { GameSpecValidationError, exactKeys, integer, record, text } from "../shared/validators";

export interface SourceGameSpec {
  readonly version: 1;
  readonly kind: "source-game";
  readonly title: string;
  readonly seed: number;
  readonly blueprint: GameBlueprint;
  readonly source: {
    readonly markup: string;
    readonly styles: string;
    readonly script: string;
  };
}

const MAX_MARKUP_LENGTH = 80_000;
const MAX_STYLES_LENGTH = 100_000;
const MAX_SCRIPT_LENGTH = 260_000;
const MAX_TOTAL_SOURCE_LENGTH = 360_000;

function sourceText(value: unknown, path: string, min: number, max: number): string {
  return text(value, path, min, max);
}

function rejectPattern(value: string, pattern: RegExp, message: string): void {
  if (pattern.test(value)) throw new GameSpecValidationError(message);
}

function validateMarkup(markup: string): void {
  rejectPattern(markup, /<\/?(?:html|head|body|script|style|link|iframe|frame|frameset|object|embed|base|meta)\b|<\/main\b/i, "source-game markup contains a forbidden element");
  rejectPattern(markup, /\son[a-z]+\s*=/i, "source-game markup must not contain inline event handlers");
  rejectPattern(markup, /\s(?:src|srcset|href|action|formaction)\s*=/i, "source-game markup must not load or navigate to resources");
  rejectPattern(markup, /https?:\/\//i, "source-game markup must not contain external URLs");
}

function validateStyles(styles: string): void {
  rejectPattern(styles, /@import\b/i, "source-game styles must not import resources");
  rejectPattern(styles, /url\s*\(/i, "source-game styles must not load resources");
  rejectPattern(styles, /https?:\/\//i, "source-game styles must not contain external URLs");
}

function validateScript(script: string): void {
  const forbidden: readonly [RegExp, string][] = [
    [/\bfetch\s*\(/i, "fetch"],
    [/\bXMLHttpRequest\b/i, "XMLHttpRequest"],
    [/\bWebSocket\b/i, "WebSocket"],
    [/\bEventSource\b/i, "EventSource"],
    [/\bsendBeacon\b/i, "sendBeacon"],
    [/\bserviceWorker\b/i, "serviceWorker"],
    [/\bSharedWorker\b|\bWorker\s*\(/i, "workers"],
    [/\bimport\s*\(|(?:^|[;\n])\s*import\s+/im, "imports"],
    [/\bwindow\s*\.\s*open\b/i, "window.open"],
    [/\b(?:window\s*\.\s*)?location\s*(?:\.|\[|=)/i, "navigation"],
    [/\b(?:top|parent|opener)\b/i, "parent-window access"],
    [/\bdocument\s*\.\s*(?:cookie|write|writeln)\b/i, "document escape APIs"],
    [/\beval\s*\(|\bFunction\s*\(/i, "dynamic code evaluation"],
    [/\b(?:localStorage|sessionStorage|indexedDB)\b/i, "direct persistent storage"],
    [/\bwhile\s*\(\s*true\s*\)|\bfor\s*\(\s*;\s*;\s*\)/i, "unbounded synchronous loops"],
    [/(?:const|let|var|function|class)\s+\$CARCADE\b/, "$CARCADE shadowing"],
    [/https?:\/\//i, "external URLs"],
  ];
  for (const [pattern, label] of forbidden) {
    rejectPattern(script, pattern, `source-game script must not use ${label}`);
  }
  if (!/(?:addEventListener\s*\(|requestAnimationFrame\s*\(|setInterval\s*\(|setTimeout\s*\()/i.test(script)) {
    throw new GameSpecValidationError("source-game script must implement interaction or a game loop");
  }
}

export function parseSourceGameSpec(value: unknown): SourceGameSpec {
  const root = record(value, "source-game spec");
  exactKeys(root, ["version", "kind", "title", "seed", "blueprint", "source"], "source-game spec");
  if (integer(root.version, "source-game spec.version", 1, 1) !== 1) {
    throw new GameSpecValidationError("source-game spec.version must be 1");
  }
  if (root.kind !== "source-game") throw new GameSpecValidationError("source-game spec.kind must be source-game");
  const title = text(root.title, "source-game spec.title", 1, 60);
  const blueprint = parseGameBlueprint(root.blueprint);
  if (title !== blueprint.title) throw new GameSpecValidationError("source-game title must match blueprint title");
  const source = record(root.source, "source-game spec.source");
  exactKeys(source, ["markup", "styles", "script"], "source-game spec.source");
  const markup = sourceText(source.markup, "source-game spec.source.markup", 20, MAX_MARKUP_LENGTH);
  const styles = sourceText(source.styles, "source-game spec.source.styles", 20, MAX_STYLES_LENGTH);
  const script = sourceText(source.script, "source-game spec.source.script", 800, MAX_SCRIPT_LENGTH);
  if (markup.length + styles.length + script.length > MAX_TOTAL_SOURCE_LENGTH) {
    throw new GameSpecValidationError("source-game source is too large");
  }
  validateMarkup(markup);
  validateStyles(styles);
  validateScript(script);
  return {
    version: 1,
    kind: "source-game",
    title,
    seed: integer(root.seed, "source-game spec.seed", 0, 2_147_483_647),
    blueprint,
    source: { markup, styles, script },
  };
}
